// Unified voice client (SignalWire Fabric SAT on web, native plugin on mobile).
//
// On iOS, Android, and web (desktop browser) → uses @signalwire/js with a
// SAT from /token. Neither mobile platform uses a native Twilio Voice SDK
// path anymore: /token mints a SignalWire Fabric SAT (see its docstring on
// the backend), not a Twilio Access Token, and the native Twilio Voice SDKs
// are hardwired to Twilio's own signaling servers regardless of token
// content - Truva's actual phone numbers only exist in SignalWire's system,
// so those SDKs can never reach them no matter what token they're given.
// SignalWire doesn't publish native mobile calling SDKs to swap in, so both
// platforms run the same @signalwire/js code as web inside the Capacitor
// WebView; only the OS-level plumbing stays native - AudioRoutePlugin
// (foreground service + audio mode) on Android, CallKitPlugin (system call
// UI + audio session ownership) on iOS.
//
// EXCEPTION, iOS, opt-in: nativeVoiceClient.js runs call media through a
// native SIP stack (linphone-sdk) instead of the WebView. That is the only
// way to get earpiece audio - WebKit pins the output route to the
// loudspeaker from its own process and no app-side API can move it (four
// approaches measured; see plans/TRUVA_MIGRATION_CLEANUP.md §2). Verified on
// device: a live native call reports outputs=["Receiver"]. Still opt-in
// (localStorage.truvaNativeVoice = '1') because the native path only covers
// outbound calls so far - incoming, conference/orchestrator and CallKit are
// still this file's job.
//
// Public API mirrors @twilio/voice-sdk's Device just enough that the rest
// of the app doesn't have to care which backend is in use.

import { Capacitor } from '@capacitor/core';
import AudioRoute from '../plugins/audioRoute';
import { callKitSafe } from '../plugins/callKit';
import { createNativeVoiceDevice, isNativeVoiceEnabled } from './nativeVoiceClient';
import { validateUsDialNumber } from './dialNumber';

export const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform();

class EventBus {
  constructor() { this._listeners = {}; }
  on(ev, cb) { (this._listeners[ev] = this._listeners[ev] || []).push(cb); return this; }
  off(ev, cb) { this._listeners[ev] = (this._listeners[ev] || []).filter(f => f !== cb); return this; }
  emit(ev, ...args) {
    (this._listeners[ev] || []).forEach(fn => {
      try { fn(...args); } catch (e) { console.error('[voiceClient] handler error:', e); }
    });
  }
}

/**
 * @signalwire/js persists Call Fabric reattach state in sessionStorage under
 * keys like as-SAT / pt-SAT / ci-SAT (SAT JWTs are encrypted so the SDK keys
 * everything by typ=SAT — shared across all users on this origin). Stale
 * authorization_state from a prior tab/session makes signalwire.connect
 * return "Requester validation failed". Clear before a fresh client connect.
 */
function clearSignalWireSessionStorage() {
  if (typeof window === 'undefined') return;
  try {
    const ss = window.sessionStorage;
    if (!ss) return;
    const doomed = [];
    for (let i = 0; i < ss.length; i += 1) {
      const key = ss.key(i);
      if (key && /^(as|pt|ci)-/i.test(key)) doomed.push(key);
    }
    doomed.forEach((key) => {
      try { ss.removeItem(key); } catch (_) {}
    });
  } catch (_) {}
}

function isRequesterValidationError(err) {
  const msg = err?.message || (typeof err === 'string' ? err : '');
  return String(msg).includes('Requester validation failed');
}

function normalizeE164(dest) {
  const raw = String(dest || '').trim();
  if (!raw) return raw;
  if (/^sip:/i.test(raw) || /truva-voice/i.test(raw)) return raw;
  const checked = validateUsDialNumber(raw);
  if (checked.ok) return checked.e164;
  // Do not invent +657… from a truncated 9-digit US number.
  return raw;
}

function mapMicError(err) {
  const name = err?.name || '';
  const msg = String(err?.message || err || '');
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return new Error('Microphone blocked - allow mic access for truva.solvit-ai.com in your browser');
  }
  if (name === 'NotFoundError' || /requested device not found/i.test(msg)) {
    // Chrome on macOS often reports NotFoundError when OS Privacy has denied
    // the browser mic - not when hardware is missing.
    return new Error(
      'Microphone unavailable - check macOS System Settings → Privacy & Security → Microphone '
      + '(enable Chrome/Safari), then site mic permission for truva.solvit-ai.com'
    );
  }
  if (name === 'NotReadableError') {
    return new Error('Microphone in use by another app - close Teams/Zoom and try again');
  }
  if (/secure context/i.test(msg) || name === 'SecurityError') {
    return new Error('Microphone requires HTTPS - use https://truva.solvit-ai.com');
  }
  return err instanceof Error ? err : new Error(msg || 'Microphone unavailable');
}

/** Reports an outgoing call to CallKit (iOS only) and waits for the shared
 * AVAudioSession to actually activate BEFORE any WebRTC media starts. MUST
 * run before client.dial() - dial() calls getUserMedia() and builds the
 * RTCPeerConnection internally, and WebKit's WebRTC audio engine binds to
 * whatever AVAudioSession state exists at the moment it begins capturing.
 * CallKit's activation reconfigures that same shared session; if dial() runs
 * first, WebKit's audio units get initialized against the pre-activation
 * session and never notice CallKit's later activation - the session reports
 * Active=YES the whole call but Recording/Playing silently stay NO.
 * Resolves to the CallKit call UUID (or null on non-iOS / CallKit unavailable). */
async function startCallKitOutgoing(handle, timeoutMs = 3000) {
  if (platform !== 'ios') return null;
  // CallKit is DISABLED BY DEFAULT on iOS, and that is deliberate.
  //
  // Calling is @signalwire/js WebRTC running inside WKWebView. When CallKit
  // activates the shared AVAudioSession for a call, WebKit treats it as an
  // audio interruption and mutes its capture pipeline - every getUserMedia
  // track reports `muted: true` and delivers no samples - and it never
  // un-mutes for the life of the call. Measured directly:
  //
  //   CallKit ON : track.muted=true,  packetsSent=0,    micEnergy=0
  //   CallKit OFF: track.muted=false, packetsSent=1001, micEnergy=2.19
  //
  // The audio session itself is configured correctly either way (category,
  // mode, route and permissions all check out) - WebKit simply stops
  // producing audio. Replacing the sender's track with a freshly acquired
  // one does not help, because the mute is applied page-wide, not per-track.
  //
  // Trade-off: without CallKit there is no system call UI, no lock-screen
  // call controls, and no native call history. Working audio wins. Restoring
  // CallKit means moving call media out of the WebView to a native SDK.
  // Set `window.__ENABLE_CALLKIT = true` to opt back in for testing.
  if (typeof window === 'undefined' || !window.__ENABLE_CALLKIT) {
    console.warn('[CallKit] disabled on iOS - WebRTC owns the audio session (see startCallKitOutgoing)');
    return null;
  }
  let callKitUuid = null;
  const activated = new Promise((resolve) => {
    if (typeof window === 'undefined') { resolve(); return; }
    const onActivated = () => {
      clearTimeout(timer);
      window.removeEventListener('callkit:audioActivated', onActivated);
      resolve();
    };
    const timer = setTimeout(() => {
      window.removeEventListener('callkit:audioActivated', onActivated);
      resolve();
    }, timeoutMs);
    window.addEventListener('callkit:audioActivated', onActivated);
  });
  await callKitSafe(async (ck) => {
    const r = await ck.startOutgoingCall({ handle: handle || 'Unknown' });
    if (r?.uuid) callKitUuid = r.uuid;
  });
  await activated;
  return callKitUuid;
}

/** Acquire (then release) the default mic so start() fails fast with a clear error. */
async function ensureMicrophone() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Browser does not support microphone access');
  }
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  } catch (e) {
    throw mapMicError(e);
  }
  try {
    stream?.getTracks?.().forEach((t) => t.stop());
  } catch (_) {}
}

class SignalWireCallAdapter extends EventBus {
  constructor(session, meta = {}) {
    super();
    this._session = session;
    this._status = 'connecting';
    this.direction = meta.direction || 'OUTGOING';
    this.parameters = {
      CallSid: meta.callSid || meta.callID || '',
      From: meta.from || '',
      To: meta.to || '',
    };
    this._ended = false;
    this._viaConference = !!meta.viaConference;
    this._remoteAnswered = false;
    this._pstnRinging = false;
    this._pollAuth = meta.pollAuth || null;
    this._orchestratorSid = meta.orchestratorSid || '';
    this._pollTimer = null;
    this._joinGraceUntil = meta.joinGraceUntil || 0;
    this._earpieceTimers = [];
    this._iceGraceTimer = null;
    this._held = false;
    // Activate call audio mode + foreground service (no-op on web/iOS -
    // see audioRoute.js's fallback; the plugin wrapper handles platform
    // detection itself, so no isNative check is needed here).
    // Android WebView WebRTC often forces speaker when media attaches -
    // AudioRoutePlugin reasserts earpiece natively; we also re-apply from
    // JS on accept / answered and on a short delay schedule.
    this._ensureEarpieceRoute('constructor');
    // iOS: report outgoing calls to CallKit so it takes over the audio
    // session (otherwise WKWebView's WebRTC defaults to the loudspeaker -
    // see callKit.js). Incoming calls are reported by the caller of
    // device.on('incoming', ...) before accept(), matching CallKit's own
    // incoming-vs-outgoing API split (no equivalent "report connected"
    // call exists for incoming - CallKit treats the CXAnswerCallAction
    // fulfillment itself as the connect signal).
    this._callKitUuid = meta.callKitUuid || null;
    // Resolves once CallKit has actually activated the shared AVAudioSession
    // for this call. Callers MUST await this before starting WebRTC media
    // (session.start()/invite.accept()) - CallKit's activation reconfigures
    // the AVAudioSession that WKWebView's WebRTC engine also uses, and doing
    // that *after* media is already flowing silences both directions.
    // Resolves immediately on non-iOS or when CallKit is unavailable.
    // If the caller already ran startCallKitOutgoing() before dial() (see
    // that function's docstring for why order matters), meta.callKitPreStarted
    // is set and this constructor must NOT repeat the CallKit dance - dial()
    // already ran by the time this constructor executes in that case, so
    // doing it here again would just be the same too-late gate as before.
    this._callKitReady = Promise.resolve();
    if (!meta.callKitPreStarted && platform === 'ios' && this.direction === 'OUTGOING') {
      this._callKitReady = (async () => {
        const activated = this._waitCallKitAudioActivated();
        await callKitSafe(async (ck) => {
          const r = await ck.startOutgoingCall({ handle: this.parameters.To || 'Unknown' });
          if (r?.uuid) this._callKitUuid = r.uuid;
        });
        await activated;
      })();
    }
    // Listen to our own events once here rather than at every emit('ringing')/
    // emit('accept') call site scattered across _handleOutboundState,
    // _markAnswered, and _wireSession's session.on('call.state', ...) handler.
    this.on('ringing', () => {
      if (this._callKitUuid) {
        callKitSafe((ck) => ck.reportOutgoingCallStartedConnecting({ uuid: this._callKitUuid }));
      }
    });
    this.on('accept', () => {
      this._ensureEarpieceRoute('accept');
      if (this._callKitUuid) {
        callKitSafe((ck) => ck.reportOutgoingCallConnected({ uuid: this._callKitUuid }));
      }
    });
    this._wireSession(session);
    // Start polling as soon as we have pollAuth, without waiting for
    // orchestratorSid - the SDK's session.on('call.state', ...) wiring
    // below never fires (the installed @signalwire/js dispatches
    // 'call.updated'/'call.connect'/'call.room'/'call.left' but silently
    // drops 'call.state' as an "unknown fabric event"), so REST polling
    // against /conference/participants is the only reliable way this
    // adapter learns the call was answered. _startConferenceAnswerPoll's
    // tick() now waits for orchestratorSid to show up on its own instead
    // of requiring it up front.
    if (this._viaConference && this._pollAuth?.apiBase) {
      this._startConferenceAnswerPoll();
    }
  }

  /** Resolves on the next 'callkit:audioActivated' event, or after timeoutMs
   * as a fallback so a missed/late CallKit callback can't hang the call. */
  _waitCallKitAudioActivated(timeoutMs = 3000) {
    if (typeof window === 'undefined') return Promise.resolve();
    return new Promise((resolve) => {
      const onActivated = () => {
        clearTimeout(timer);
        window.removeEventListener('callkit:audioActivated', onActivated);
        resolve();
      };
      const timer = setTimeout(() => {
        window.removeEventListener('callkit:audioActivated', onActivated);
        resolve();
      }, timeoutMs);
      window.addEventListener('callkit:audioActivated', onActivated);
    });
  }

  /**
   * Force earpiece on Android after WebRTC media attaches. The native
   * AudioRoutePlugin also reasserts; this covers the JS-side call lifecycle
   * (constructor → accept → answered) where SignalWire flips speaker.
   * No-op on web; on iOS CallKit owns routing.
   */
  _ensureEarpieceRoute(reason = '') {
    if (platform !== 'android') return;
    this._clearEarpieceTimers();
    const apply = (forceEarpiece) => {
      if (this._ended) return;
      AudioRoute.setCallMode({ active: true }).catch((e) => {
        console.warn('[SignalWireCall] setCallMode(active) failed:', e?.message || e);
      });
      // Only force speaker OFF on the first apply. Delayed re-applies must
      // not undo a mid-call speaker toggle - native AudioRoutePlugin keeps
      // reasserting earpiece while speakerWanted=false.
      if (forceEarpiece) {
        AudioRoute.setSpeaker({ enabled: false }).catch((e) => {
          console.warn('[SignalWireCall] setSpeaker(false) failed:', e?.message || e);
        });
      }
      if (reason) {
        console.log('[SignalWireCall] earpiece route applied:', reason, 'force=', !!forceEarpiece);
      }
    };
    apply(true);
    // WebRTC attach is async - nudge native reassert after media-start delays.
    for (const ms of [200, 600, 1200, 2500]) {
      const t = setTimeout(() => apply(false), ms);
      this._earpieceTimers.push(t);
    }
  }

  _clearEarpieceTimers() {
    for (const t of this._earpieceTimers || []) clearTimeout(t);
    this._earpieceTimers = [];
  }

  _eventParams(ev) {
    return ev?.params || ev || {};
  }

  _deviceType(params) {
    return params?.device?.type || null;
  }

  _handleOutboundState(state, deviceType) {
    if (state === 'ringing') {
      if (deviceType === 'phone' || !deviceType) {
        this._pstnRinging = true;
        if (this._status !== 'open') {
          this._status = 'ringing';
          this.emit('ringing');
        }
      }
      return;
    }
    if (state === 'answered' || state === 'active') {
      // Browser/WebRTC leg joined the conference - audio IS flowing from the
      // user's end to the bridge, even if the PSTN phone target hasn't answered
      // yet. Mark as answered so the translate button (gated behind
      // callConnected) unlocks as soon as the user is on the call.
      this._markAnswered();
      return;
    }
    if (state === 'ended' || state === 'ending') {
      this._finish();
    }
  }

  _markAnswered() {
    if (this._remoteAnswered || this._ended) return;
    this._remoteAnswered = true;
    this._status = 'open';
    this._stopConferenceAnswerPoll();
    this.emit('accept', this);
  }

  _startConferenceAnswerPoll() {
    this._stopConferenceAnswerPoll();
    const tick = async () => {
      if (this._ended || this._remoteAnswered) return;
      const { apiBase, authToken } = this._pollAuth || {};
      if (!apiBase || !authToken) return; // no auth to poll with - nothing more we can do
      if (!this._orchestratorSid) {
        // _resolveOrchestratorSid hasn't found it yet (up to ~20s of its
        // own retries) - keep waiting instead of dying after one no-op
        // tick, which used to silently kill answer-detection for the
        // rest of the call.
        this._pollTimer = setTimeout(tick, 1000);
        return;
      }
      try {
        const res = await fetch(
          `${apiBase}/conference/participants?call_sid=${encodeURIComponent(this._orchestratorSid)}`,
          { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } }
        );
        if (res.ok) {
          const json = await res.json().catch(() => null);
          const parts = Array.isArray(json) ? json : (json?.participants || []);
          // Moderator + answered PSTN leg = 2 participants in the room.
          if (parts.length >= 2) {
            this._markAnswered();
            return;
          }
          if (!this._remoteAnswered && this._status !== 'ringing') {
            this._status = 'ringing';
            this.emit('ringing');
          }
        } else {
          console.warn('[SignalWireCall] conference/participants poll HTTP', res.status);
        }
      } catch (e) {
        console.warn('[SignalWireCall] conference/participants poll error:', e?.message || e);
      }
      if (!this._ended && !this._remoteAnswered) {
        this._pollTimer = setTimeout(tick, 1500);
      }
    };
    this._pollTimer = setTimeout(tick, 1000);
  }

  _stopConferenceAnswerPoll() {
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
  }

  _wireSession(session) {
    if (!session || typeof session.on !== 'function') return;
    session.on('call.state', (ev) => {
      const params = this._eventParams(ev);
      const state = params.call_state;
      const deviceType = this._deviceType(params);
      const id = params.call_id;
      if (id) this.parameters.CallSid = id;
      if (this.direction === 'OUTGOING' && !this._viaConference) {
        if ((state === 'ringing' || state === 'connecting' || state === 'created') && this._status !== 'open') {
          this._status = 'ringing';
          this.emit('ringing');
        } else if (state === 'answered' || state === 'active') {
          this._status = 'open';
          this.emit('accept', this);
        } else if (state === 'ended' || state === 'ending') {
          this._finish();
        }
        return;
      }
      if (this.direction === 'OUTGOING' && this._viaConference) {
        this._handleOutboundState(state, deviceType);
        return;
      }
      if (state === 'ringing') {
        this._status = 'ringing';
        this.emit('ringing');
      } else if (state === 'answered' || state === 'active') {
        this._status = 'open';
        this.emit('accept', this);
      } else if (state === 'ended' || state === 'ending') {
        this._finish();
      }
    });
    session.on('call.joined', (ev) => {
      const id = this._eventParams(ev).call_id;
      if (id) this.parameters.CallSid = id;
    });
    session.on('destroy', () => this._finish());
    session.on('hangup', () => this._finish());
    // Web interruption survival: browsers cannot see other apps' calls, so we
    // ride out mic-mute / ICE drops instead of treating them as hangup.
    this._attachInterruptionGuards(session);
  }

  // ---------------------------------------------------------------------------
  // Interruption survival (web + iOS WebView browser leg).
  //
  // Browsers cannot see other apps' calls. When the local mic track is muted
  // or the ICE path drops mid-call (WhatsApp/cellular/second Truva call), the
  // only thing we can do is surface the held state, keep the session OPEN, try
  // to recover, and only END the call if recovery actually fails after a grace
  // period. A brief interruption must never be mistaken for a hangup.
  // ---------------------------------------------------------------------------

  /** Drain grace (ms) before giving up on a dead ICE path and ending the call. */
  ICE_GRACE_MS = 20000;

  /** Local held flag mirror. isHeld() is true while the call is on hold. */
  _setHeld(held) {
    held = !!held;
    if (this._held === held) return;
    this._held = held;
    this.emit(held ? 'hold' : 'unhold', this);
  }

  isHeld() { return !!this._held; }

  /** Real hold is driven server-side via /call/hold; this reflects local held
   *  state (used when an interruption mutes the mic). Native paths (iOS/Android)
   *  drive hold through the native plugin instead. */
  setHeld(held) {
    this._setHeld(!!held);
  }

  /** Wire web-side interruption guards to the live RTCPeerConnection: local
   *  mic mute/unmute/ended and ICE connection state. No-op outside a browser
   *  or before the peer is available. */
  _attachInterruptionGuards(session) {
    if (!session || typeof window === 'undefined') return;
    const pc = session.peer?.instance;
    const localTrack = session.peer?.localAudioTrack
      || (pc ? Array.from((pc.getSenders && pc.getSenders()) || [])
          .map((s) => s.track).find((t) => t && t.kind === 'audio') : undefined);
    if (localTrack && typeof localTrack.addEventListener === 'function') {
      const onMute = () => { if (this._ended) return; this._setHeld(true); };
      const onUnmute = () => { if (this._ended) return; this._setHeld(false); };
      const onEnded = () => { this._handleMicTrackEnded(); };
      localTrack.addEventListener('mute', onMute);
      localTrack.addEventListener('unmute', onUnmute);
      localTrack.addEventListener('ended', onEnded);
      localTrack.__truvaGuardCleanup = () => {
        localTrack.removeEventListener('mute', onMute);
        localTrack.removeEventListener('unmute', onUnmute);
        localTrack.removeEventListener('ended', onEnded);
      };
    }
    if (pc && typeof pc.addEventListener === 'function') {
      const onIce = () => { this._handleIceState(pc); };
      pc.addEventListener('iceconnectionstatechange', onIce);
      pc.__truvaIceCleanup = () => pc.removeEventListener('iceconnectionstatechange', onIce);
      // Seed once in case the state is already degraded when we attach.
      this._handleIceState(pc);
    }
  }

  /** React to ICE connection-state changes. `disconnected`/`failed` are often
   *  transient (network blip, another app stealing the audio path) — hold the
   *  UI, try an ICE restart, and only end the call if the path is STILL dead
   *  after the grace period. `connected`/`completed` clears held and cancels
   *  any pending grace timer. */
  _handleIceState(pc) {
    if (this._ended || !pc) return;
    const st = (pc.connectionState || pc.iceConnectionState || '');
    if (st === 'disconnected' || st === 'failed') {
      if (this._iceGraceTimer) return; // already counting down
      this._setHeld(true);
      console.warn(`[SignalWireCall] ICE ${st} — starting ${this.ICE_GRACE_MS / 1000}s recovery grace`);
      const attemptRestart = () => {
        if (this._ended) return;
        try { this._session?.peer?.restartIce?.() || this._session?.peer?.restartIceWithRelayOnly?.(); } catch (_) {}
      };
      this._iceGraceTimer = setTimeout(() => {
        this._iceGraceTimer = null;
        const now = (pc.connectionState || pc.iceConnectionState || '');
        if (now === 'disconnected' || now === 'failed') {
          console.error(`[SignalWireCall] ICE STILL ${now} after grace — ending call`);
          this._finish();
        }
      }, this.ICE_GRACE_MS);
      attemptRestart();
    } else if (st === 'connected' || st === 'completed') {
      if (this._iceGraceTimer) { clearTimeout(this._iceGraceTimer); this._iceGraceTimer = null; }
      this._setHeld(false);
    }
  }

  /** The local mic track died (another app took exclusive capture, device
   *  removal). Re-acquire a fresh mic and replaceTrack on the sender. If that
   *  isn't possible, stay held but keep the session open. Never hang up. */
  async _handleMicTrackEnded() {
    if (this._ended) return;
    const pc = this._session?.peer?.instance;
    if (!pc || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this._setHeld(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const track = stream.getAudioTracks && stream.getAudioTracks()[0];
      const senders = (pc.getSenders && pc.getSenders()) || [];
      const audioSender = senders.find((s) => s.track && s.track.kind === 'audio') || senders[0];
      if (audioSender && typeof audioSender.replaceTrack === 'function') {
        await audioSender.replaceTrack(track || null);
        // Old dead track replaced; re-arm mute/unmute guards on the new one.
        this._attachInterruptionGuards(this._session);
        this._setHeld(false);
        return;
      }
    } catch (e) {
      console.warn('[SignalWireCall] mic re-acquire after interruption failed:', e?.message || e);
    }
    this._setHeld(true);
  }

  _finish() {
    if (this._ended) return;
    if (this._viaConference && this._joinGraceUntil && Date.now() < this._joinGraceUntil) {
      // Conference-join grace. While this leg is being redirected into the
      // conference the SDK fires a spurious `disconnect` even though the
      // bridge is healthy, so a disconnect in this window cannot be taken at
      // face value.
      //
      // It must not be silently dropped either, which is what used to happen:
      // a genuine far-end hangup landing inside the window left the UI on
      // "Connected" - timer still counting - until some later event happened
      // to arrive after the window expired. Measured at ~20s of dead air.
      //
      // Emit a distinct signal instead and let the listener adjudicate
      // against the backend, which can tell the two cases apart: mid-join the
      // leg is still in-progress, whereas a real hangup has already completed
      // it. The listener calls finishConfirmed() if it turns out to be real.
      console.warn('[SignalWireCall] disconnect during conference join grace - deferring to backend verification');
      this.emit('graceDisconnect', this);
      return;
    }
    this._ended = true;
    this._held = false;
    this._iceGraceTimer = null;
    this._stopConferenceAnswerPoll();
    this._clearEarpieceTimers();
    this._status = 'closed';
    try { this._session?.peer?.localAudioTrack?.__truvaGuardCleanup?.(); } catch (_) {}
    try { this._session?.peer?.instance?.__truvaIceCleanup?.(); } catch (_) {}
    AudioRoute.setCallMode({ active: false }).catch((e) => {
      console.warn('[SignalWireCall] setCallMode(inactive) failed:', e?.message || e);
    });
    if (this._callKitUuid) {
      callKitSafe((ck) => ck.endCall({ uuid: this._callKitUuid }));
    }
    this.emit('disconnect', this);
  }

  /**
   * Complete a disconnect that arrived during the conference-join grace and
   * that the caller has since confirmed with the backend is real. Clears the
   * grace so _finish() runs its normal teardown instead of deferring again.
   */
  finishConfirmed() {
    this._joinGraceUntil = 0;
    this._finish();
  }

  status() { return this._status; }

  accept() {
    this._status = 'open';
    this._ensureEarpieceRoute('accept()');
    this.emit('accept', this);
  }

  reject() {
    try { this._session?.end?.(); } catch (_) {}
    this._finish();
  }

  disconnect() {
    try { this._session?.end?.(); } catch (e) { console.warn('[SignalWireCall] disconnect:', e); }
    this._finish();
  }

  mute(m) {
    const fn = m ? this._session?.audioMute : this._session?.audioUnmute;
    if (typeof fn === 'function') fn.call(this._session).catch(() => {});
  }

  sendDigits(d) {
    const digits = String(d ?? '');
    if (!digits) return;
    // @signalwire/js SAT sessions do not implement sendDigits — the
    // in-app keypad used to echo locally and never reach the PSTN IVR.
    if (typeof this._session?.sendDigits === 'function') {
      this._session.sendDigits(digits).catch(() => {});
    }
    const auth = this._pollAuth || {};
    const sid = this._orchestratorSid || this.parameters?.CallSid || '';
    if (auth.apiBase && auth.authToken && sid) {
      fetch(`${auth.apiBase}/api/call/dtmf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth.authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ call_sid: sid, digits }),
      }).catch((e) => console.warn('[SignalWireCall] dtmf failed:', e?.message || e));
    }
  }
}

class SignalWireWebDevice extends EventBus {
  constructor(token, opts = {}) {
    super();
    this._token = token;
    this._opts = opts || {};
    this._client = null;
    this._ready = false;
    this._currentCall = null;
    this._pendingIncomingCall = null;
    this._destroyed = false;
    this._outboundRingWaiter = null;
    this._connectPromise = null;
    this._initPromise = this._init();
    // Android's notification "Hang Up" button (VoiceCallService.ACTION_HANGUP)
    // can't reach the Twilio SDK when there isn't one active - MainActivity
    // relays it into the WebView as this CustomEvent instead.
    if (typeof window !== 'undefined') {
      window.addEventListener('truvaHangupRequested', () => {
        try { this._currentCall?.disconnect?.(); } catch (e) {
          console.warn('[SignalWireCall] hangup-broadcast disconnect failed:', e?.message || e);
        }
      });
      // iOS: CallKitManager dispatches these when the user acts on the
      // system call UI (lock screen / Dynamic Island) rather than the app's
      // own UI - forward them to whichever call is actually active. answer
      // targets the pending incoming call specifically since there's no
      // "current" call yet at that point.
      window.addEventListener('callkit:answer', () => {
        try { this._pendingIncomingCall?.accept?.(); } catch (e) {
          console.warn('[SignalWireCall] callkit:answer accept failed:', e?.message || e);
        }
      });
      window.addEventListener('callkit:end', () => {
        try {
          if (this._pendingIncomingCall) this._pendingIncomingCall.reject?.();
          else this._currentCall?.disconnect?.();
        } catch (e) {
          console.warn('[SignalWireCall] callkit:end failed:', e?.message || e);
        }
      });
      window.addEventListener('callkit:mute', (ev) => {
        try { this._currentCall?.mute?.(!!ev?.detail?.muted); } catch (e) {
          console.warn('[SignalWireCall] callkit:mute failed:', e?.message || e);
        }
      });
      window.addEventListener('callkit:dtmf', (ev) => {
        try { if (ev?.detail?.digits) this._currentCall?.sendDigits?.(ev.detail.digits); } catch (e) {
          console.warn('[SignalWireCall] callkit:dtmf failed:', e?.message || e);
        }
      });
    }
  }

  _waitSessionReady(session, timeoutMs = 45000) {
    if (!session || typeof session.on !== 'function') {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('WebRTC setup timeout - check mic permission and network'));
      }, timeoutMs);
      const finish = () => {
        clearTimeout(timer);
        resolve();
      };
      const fail = () => {
        clearTimeout(timer);
        reject(new Error('Call session ended before media was ready'));
      };
      const onState = (ev) => {
        const params = ev?.params || ev || {};
        const state = params.call_state || params.state;
        if (state === 'answered' || state === 'active') {
          finish();
        }
      };
      session.on('call.state', onState);
      session.once('room.subscribed', finish);
      session.once('destroy', fail);
      session.once('hangup', fail);
    });
  }

  _acceptInvite(invite, meta = {}) {
    const details = invite?.details || {};
    const call = new SignalWireCallAdapter(null, {
      direction: meta.direction || 'OUTGOING',
      // Prefer the orchestrator sid (names the Room_* conference and is what
      // the backend - translate, participants poll - keys everything on);
      // the SDK's own callID is a different id that resolves nothing there.
      callSid: meta.callSid || details.callID,
      from: meta.from || details.caller_id_number || details.caller_id_name || '',
      to: meta.to || details.callee_id_number || '',
    });
    return (async () => {
      await ensureMicrophone();
      await call._callKitReady;
      const session = await invite.accept({ audio: true, video: false });
      // @signalwire/js accept() already invokes answer() internally -
      // calling session.answer() again re-executes verto.answer against a
      // call that has already progressed, spamming "CALL DOES NOT EXIST"
      // (-32002) and poisoning the session state. _waitSessionReady below
      // observes the answered/active state instead.
      call._session = session;
      call._wireSession(session);
      try {
        await this._waitSessionReady(session);
      } catch (e) {
        console.warn('[SignalWireCall] session ready wait:', e?.message || e);
      }
      call._status = 'connecting';
      return call;
    })();
  }

  _waitForOutboundRing(timeoutMs = 45000) {
    if (this._outboundRingWaiter) {
      return Promise.reject(new Error('outbound already in progress'));
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this._outboundRingWaiter) {
          this._outboundRingWaiter = null;
          reject(new Error('Timed out waiting for browser ring (check mic + Online status)'));
        }
      }, timeoutMs);
      this._outboundRingWaiter = {
        resolve: (call) => { clearTimeout(timer); resolve(call); },
        reject: (err) => { clearTimeout(timer); reject(err); },
      };
    });
  }

  async _disconnectClient() {
    const client = this._client;
    this._client = null;
    this._ready = false;
    if (!client) return;
    try { await client.offline?.(); } catch (_) {}
    try { await client.disconnect?.(); } catch (_) {}
  }

  /**
   * Create the Fabric client. Do NOT pass space_url as `host` — SAT tokens
   * omit the JWT `ch` claim, and the SDK's default WebSocket host is
   * wss://relay.signalwire.com. Forcing the Space portal host made
   * signalwire.connect return "Requester validation failed" in browsers.
   * Only honor an explicit opts.host (tests / rare overrides).
   */
  async _connectClient() {
    const { SignalWire } = await import('@signalwire/js');
    const onRefreshToken = this._opts.onRefreshToken;
    clearSignalWireSessionStorage();
    const rawHost = this._opts.host || '';
    const host = String(rawHost).replace(/^https?:\/\//, '').replace(/\/$/, '') || undefined;
    const params = {
      token: this._token,
      // Fresh softphone register should not resume a prior Fabric room
      // protocol / authorization_state from sessionStorage.
      reattach: false,
      logLevel: this._opts.logLevel || 'warn',
      onRefreshToken: onRefreshToken
        ? async () => {
          const next = await onRefreshToken();
          if (next) this._token = next;
          return next;
        }
        : undefined,
    };
    if (host) params.host = host;
    this._client = await SignalWire(params);
    this._ready = true;
  }

  async _init() {
    try {
      await this._connectClient();
    } catch (err) {
      if (!isRequesterValidationError(err) || this._destroyed) throw err;
      console.warn('[SignalWireCall] connect Requester validation failed — clearing SAT session and retrying once');
      clearSignalWireSessionStorage();
      await this._disconnectClient();
      await this._connectClient();
    }
  }

  async _waitReady() {
    await this._initPromise;
    if (this._destroyed) throw new Error('device destroyed');
  }

  async _goOnline() {
    await this._client.online({
      incomingCallHandlers: {
        websocket: (notification) => {
          const invite = notification?.invite;
          if (!invite) return;
          const details = invite.details || {};
          const waiter = this._outboundRingWaiter;
          if (waiter) {
            this._outboundRingWaiter = null;
            this._acceptInvite(invite, {
              direction: 'OUTGOING',
              callSid: this._pendingOrchestratorSid || undefined,
              from: details.caller_id_number || details.caller_id_name || '',
              to: details.callee_id_number || '',
            })
              .then((call) => waiter.resolve(call))
              .catch((err) => waiter.reject(mapMicError(err)));
            return;
          }
          const pending = new SignalWireCallAdapter(null, {
            direction: 'INCOMING',
            callSid: details.callID,
            from: details.caller_id_number || details.caller_id_name || 'Unknown',
            to: details.callee_id_number || '',
          });
          pending._pendingInvite = invite;
          pending.accept = async () => {
            try {
              const call = await this._acceptInvite(invite, {
                direction: 'INCOMING',
                from: details.caller_id_number || details.caller_id_name || 'Unknown',
                to: details.callee_id_number || '',
              });
              pending._session = call._session;
              pending._wireSession(call._session);
              pending._status = 'open';
              this._pendingIncomingCall = null;
              pending.emit('accept', pending);
            } catch (e) {
              const mapped = mapMicError(e);
              this._pendingIncomingCall = null;
              pending.emit('error', mapped);
              pending._finish();
            }
          };
          pending.reject = async () => {
            try { await invite.reject(); } catch (_) {}
            this._pendingIncomingCall = null;
            pending._finish();
          };
          this._pendingIncomingCall = pending;
          this._currentCall = pending;
          this.emit('incoming', pending);
        },
      },
    });
  }

  async register() {
    await this._waitReady();
    try {
      await this._client.offline?.();
    } catch (_) {}
    try {
      await this._goOnline();
    } catch (err) {
      const detail = err?.message || err?.original_request?.method || String(err);
      console.error('[SignalWireCall] online/register failed:', detail, err);
      // Fully tear down so @signalwire/js's module singleton can accept a
      // new token on Retry Connect (disconnect resets that singleton).
      await this._disconnectClient();
      clearSignalWireSessionStorage();
      if (isRequesterValidationError(err) && !this._destroyed) {
        try {
          console.warn('[SignalWireCall] online Requester validation — reconnecting once');
          this._initPromise = this._connectClient();
          await this._waitReady();
          await this._goOnline();
          this.emit('registered');
          return;
        } catch (retryErr) {
          await this._disconnectClient();
          throw retryErr;
        }
      }
      throw err;
    }
    this.emit('registered');
  }

  updateToken(token) {
    this._token = token;
    if (this._client && typeof this._client.updateToken === 'function') {
      return this._client.updateToken(token);
    }
    return Promise.resolve();
  }

  destroy() {
    this._destroyed = true;
    try { this._currentCall?.disconnect?.(); } catch (_) {}
    this._currentCall = null;
    // Fire-and-forget but keep the promise so Retry Connect can await a
    // clean singleton reset before minting a new client.
    this._destroyPromise = this._disconnectClient();
    return this._destroyPromise;
  }

  async _connectDirectPstn(params) {
    const dest = normalizeE164(params?.To);
    const callerId = normalizeE164(params?.CallerId);
    const dialParams = {
      to: dest,
      audio: true,
      video: false,
      negotiateVideo: false,
    };
    if (params?.fromFabricAddressId) {
      dialParams.fromFabricAddressId = params.fromFabricAddressId;
    }
    // CallKit must activate the AVAudioSession before ANY getUserMedia call,
    // including this throwaway pre-flight one - see startCallKitOutgoing's
    // docstring. A getUserMedia call before activation can bind WebKit's
    // audio engine to the pre-activation session even if the resulting
    // stream is immediately stopped.
    const callKitUuid = await startCallKitOutgoing(dest);
    await ensureMicrophone();
    const session = await this._client.dial(dialParams);
    const call = new SignalWireCallAdapter(session, {
      direction: 'OUTGOING',
      to: dest,
      from: callerId || '',
      callKitUuid,
      callKitPreStarted: true,
    });
    await session.start();
    return call;
  }

  _resolveOrchestratorSid(call, truvaSession, { apiBase, authToken }) {
    // /voice assigns this leg's cXML CallSid to the pending session once
    // the dialed fabric call lands there - poll until it shows up so the
    // UI (translate, participants) keys off the sid that names Room_*.
    // Canonical query param is truva_session.
    (async () => {
      for (let i = 0; i < 20; i++) {
        if (call._ended) return;
        try {
          const r = await fetch(
            `${apiBase}/api/outbound/orchestrator?truva_session=${encodeURIComponent(truvaSession)}`,
            { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } },
          );
          if (r.ok) {
            const j = await r.json();
            if (j.orchestrator_call_sid) {
              call.parameters.CallSid = j.orchestrator_call_sid;
              call._orchestratorSid = j.orchestrator_call_sid;
              console.log('[SignalWireCall] orchestrator sid resolved', j.orchestrator_call_sid);
              if (call._pollAuth?.apiBase && !call._pollTimer) {
                call._startConferenceAnswerPoll();
              }
              return;
            }
          }
        } catch (_) {}
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      console.warn('[SignalWireCall] orchestrator sid not resolved for', truvaSession);
    })();
  }

  async _connectViaConference(params, auth) {
    const apiBase = auth?.apiBase || this._opts.apiBase;
    const authToken = auth?.authToken || this._opts.authToken;
    if (!apiBase || !authToken) {
      throw new Error('voiceClient: missing apiBase/authToken for conference outbound');
    }
    const dest = normalizeE164(params?.To);
    const callerId = normalizeE164(params?.CallerId);
    const ringPromise = this._waitForOutboundRing();
    const res = await fetch(`${apiBase}/api/outbound/start`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({ to: dest, caller_id: callerId }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      const errMsg = errBody.message || errBody.error || `outbound/start HTTP ${res.status}`;
      if (this._outboundRingWaiter) {
        this._outboundRingWaiter.reject(new Error(errMsg));
        this._outboundRingWaiter = null;
      }
      void ringPromise.catch(() => {});
      throw new Error(errMsg);
    }
    const body = await res.json();
    if (body.mode === 'client_dial' && body.dial_address) {
      // New flow: the browser dials the truva-voice cXML webhook fabric
      // resource itself - /voice claims the pending session, joins this
      // leg to the conference, and dials the PSTN target. (Backend can't
      // ring the browser: Calling API can't run cXML, LaML can't reach
      // fabric SAT clients.)
      if (this._outboundRingWaiter) this._outboundRingWaiter = null;
      void ringPromise.catch(() => {});
      const voiceSession = body.truva_session;
      console.log('[SignalWireCall] conference outbound via client dial', body.dial_address, voiceSession);
      // CallKit must activate the AVAudioSession before ANY getUserMedia
      // call, including this throwaway pre-flight one - see
      // startCallKitOutgoing's docstring.
      const callKitUuid = await startCallKitOutgoing(dest);
      await ensureMicrophone();
      const session = await this._client.dial({
        to: body.dial_address,
        audio: true,
        video: false,
        negotiateVideo: false,
      });
      const call = new SignalWireCallAdapter(session, {
        direction: 'OUTGOING',
        to: dest,
        from: callerId || '',
        viaConference: true,
        joinGraceUntil: Date.now() + 20000,
        pollAuth: { apiBase, authToken },
        callKitUuid,
        callKitPreStarted: true,
      });
      await session.start();
      this._resolveOrchestratorSid(call, voiceSession, { apiBase, authToken });
      if (!call._remoteAnswered && call._status !== 'open') {
        call._status = 'ringing';
        call.emit('ringing');
      }
      return call;
    }
    console.log('[SignalWireCall] conference outbound started', body.orchestrator_call_sid);
    // Stash before the invite arrives so _acceptInvite bakes the
    // orchestrator sid into the adapter's CallSid at construction -
    // React reads it at render time and never sees a later mutation.
    this._pendingOrchestratorSid = body.orchestrator_call_sid || '';
    let call;
    try {
      call = await ringPromise;
    } finally {
      this._pendingOrchestratorSid = '';
    }
    if (body.orchestrator_call_sid) {
      call.parameters.CallSid = body.orchestrator_call_sid;
    }
    call._viaConference = true;
    call._orchestratorSid = body.orchestrator_call_sid || '';
    call._joinGraceUntil = Date.now() + 20000;
    call._pollAuth = { apiBase, authToken };
    if (call._pollAuth?.apiBase && call._orchestratorSid) {
      call._startConferenceAnswerPoll();
    }
    // Browser leg is in the conference - unlock translate UI even while PSTN rings.
    if (!call._remoteAnswered && call._status !== 'open') {
      call._status = 'ringing';
      call.emit('ringing');
    }
    return call;
  }

  /**
   * Inbound softphone answer: dial the truva-voice fabric resource so /voice
   * joins the PSTN conference. LaML cannot push invites to Fabric SAT
   * clients - the browser must dial in (same constraint as outbound).
   */
  async connectInboundJoin(dialAddress, meta = {}) {
    await this._waitReady();
    if (!dialAddress) throw new Error('voiceClient: missing dialAddress for inbound join');
    if (this._currentCall) {
      try { this._currentCall.disconnect(); } catch (_) {}
      this._currentCall = null;
    }
    // Keep the ring-time warm mic LIVE through dial()/start(). Stopping it
    // first forced a second getUserMedia open and added ~1s before WebRTC
    // could send audio. Release warm tracks only after start() has begun.
    const warm = typeof window !== 'undefined' ? window.__truvaWarmMic : null;
    const hasWarm = Boolean(warm?.getTracks?.().some((t) => t.readyState === 'live'));
    if (!hasWarm) {
      await ensureMicrophone();
    }
    const releaseWarm = () => {
      if (!hasWarm || !warm) return;
      try {
        warm.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      try { window.__truvaWarmMic = null; } catch (_) {}
    };
    const session = await this._client.dial({
      to: dialAddress,
      audio: true,
      video: false,
      negotiateVideo: false,
    });
    const call = new SignalWireCallAdapter(session, {
      direction: 'INCOMING',
      from: meta.from || '',
      to: meta.to || '',
      callSid: meta.callSid || '',
      // Not viaConference for grace purposes - inbound join has no
      // /call/add redirect race, and a 20s grace was swallowing PSTN
      // hangup (web stayed "Connected").
      viaConference: false,
      joinGraceUntil: 0,
      pollAuth: {
        apiBase: this._opts.apiBase,
        authToken: this._opts.authToken,
      },
    });
    call._wireSession(session);
    // Kick WebRTC immediately; release warm mic once start is in flight so
    // dial's own capture track owns the device.
    const startP = session.start();
    // Mute before media flows when pre-joining during ring - Accept unmutes.
    if (meta.muted) {
      try { call.mute(true); } catch (_) {}
      startP.then(() => {
        try { call.mute(true); } catch (_) {}
      }).catch(() => {});
    }
    startP.catch((e) => {
      const msg = e?.message || String(e);
      console.warn('[SignalWireCall] inbound join session.start:', msg);
      if (/uri_does_not_resolve|Unrecognized address|422/i.test(msg)) {
        console.error(
          '[SignalWireCall] Fabric dial address is not registered in SignalWire. '
          + `Tried: ${dialAddress}. Create a cXML Application resource named `
          + 'truva-voice (public) pointing at https://truva.solvit-ai.com/voice, '
          + 'or set TRUVA_VOICE_FABRIC_ADDRESS to the live resource address.'
        );
      }
    }).finally(releaseWarm);
    // Safety: if start() hangs, don't hold the warm device forever.
    setTimeout(releaseWarm, 4000);
    call._status = 'open';
    call.emit('accept', call);
    this._currentCall = call;
    return call;
  }

  async connect({ params, token, apiBase, authToken } = {}) {
    if (this._connectPromise) {
      return this._connectPromise;
    }
    const inFlight = this._runConnect({ params, token, apiBase, authToken });
    this._connectPromise = inFlight;
    try {
      return await inFlight;
    } finally {
      if (this._connectPromise === inFlight) this._connectPromise = null;
    }
  }

  async _runConnect({ params, token, apiBase, authToken } = {}) {
    await this._waitReady();
    if (token) await this.updateToken(token);
    const dest = normalizeE164(params?.To);
    if (!dest) throw new Error('voiceClient: missing To param');
    if (this._outboundRingWaiter) {
      this._outboundRingWaiter.reject(new Error('superseded by new outbound call'));
      this._outboundRingWaiter = null;
    }
    if (this._currentCall) {
      try { this._currentCall.disconnect(); } catch (_) {}
      this._currentCall = null;
    }
    const useConference = params?.useConference !== false;
    const auth = { apiBase: apiBase || this._opts.apiBase, authToken: authToken || this._opts.authToken };
    let call;
    try {
      if (useConference && auth.apiBase && auth.authToken) {
        try {
          call = await this._connectViaConference(params, auth);
        } catch (e) {
          // Do NOT fall back to a second PSTN dial. /api/outbound/start +
          // /voice may already have the callee's phone ringing. A second
          // direct dial is how one tap became two 520→574 calls.
          console.warn('[SignalWireCall] conference outbound failed (no PSTN fallback):', e?.message || e);
          throw e;
        }
      } else {
        call = await this._connectDirectPstn(params);
      }
    } catch (e) {
      const mapped = mapMicError(e);
      console.error('[SignalWireCall] connect failed:', mapped.message || e);
      throw mapped;
    }
    this._currentCall = call;
    return call;
  }
}

/**
 * Factory. Returns a Device-like object.
 */
export async function createVoiceDevice(token, opts = {}) {
  if (
    typeof window !== 'undefined' &&
    window.__TRUVA_E2E_MOCK_DEVICE === true &&
    !isNative
  ) {
    const stub = new EventBus();
    stub.destroy = () => {};
    stub.updateToken = () => {};
    stub.register = async () => {};
    stub.connect = async () => { throw new Error('voiceClient stub: connect() not supported under E2E mock'); };
    setTimeout(() => stub.emit('registered'), 0);
    return stub;
  }

  if (isNativeVoiceEnabled()) {
    try {
      console.warn('[voiceClient] native SIP path enabled - call media will bypass the WebView');
      return await createNativeVoiceDevice(opts);
    } catch (e) {
      // Never strand the user on a broken opt-in path: fall back to the
      // WebView device, which works apart from the loudspeaker routing.
      console.error('[voiceClient] native voice init failed, falling back to WebView:', e);
    }
  }

  return new SignalWireWebDevice(token, opts);
}
